// src/components/ErrorBoundary.jsx
//
// Class component — React error boundaries must be class-based.
// Wraps any subtree; catches render/lifecycle errors and shows
// a graceful fallback instead of a white screen.
//
// Usage:
//   <ErrorBoundary label="Triage report">
//     <DoctorTriageView ... />
//   </ErrorBoundary>
//
//   // Custom fallback:
//   <ErrorBoundary fallback={<p>Something went wrong</p>}>
//     <RiskyComponent />
//   </ErrorBoundary>
//
//   // Root of the tree — nothing above it to catch anything, so it also
//   // offers a hard reload and takes over the viewport:
//   <ErrorBoundary label="Previu" root />
//
// The error detail used to be hidden in production. That was the wrong call:
// the people who hit these are staff reporting a problem over WhatsApp, and
// "something went wrong" gives them nothing to send us. Two blank-screen
// reports in a row cost several rounds each purely because the message and the
// build it came from had to be reconstructed from screenshots of the console.
// The detail is collapsed by default and names no patient data — it is the
// exception message and the build SHA — so showing it costs nothing and turns
// a report into a diagnosis.

import React from 'react';
import { BUILD_SHA_SHORT, BUILD_TIME } from '../buildInfo';
import Icon from './Icon';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Always logged, production included — see the note at the top of the file.
    console.error('[ErrorBoundary]', this.props.label || '', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  // A deterministic render error re-throws the instant "Try again" re-mounts
  // the same subtree, so a hard reload is the only move that actually helps —
  // and it is the one that clears a stale cached bundle, which is a live
  // suspect whenever a screen goes blank right after a deploy.
  handleReload = () => { window.location.reload(); };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Custom fallback prop takes priority
    if (this.props.fallback) return this.props.fallback;

    const label = this.props.label || 'This section';
    const isRoot = !!this.props.root;

    const panel = (
      <div style={{
        padding: '32px 24px',
        background: 'var(--red-bg)',
        border: '1px solid var(--red)',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        textAlign: 'center',
        maxWidth: 460,
        width: '100%',
      }}>
        <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="alert" size={28} /></div>
        <div style={{
          fontSize: 15, fontWeight: 600,
          color: 'var(--red)',
          fontFamily: 'var(--font-display)',
        }}>
          {isRoot ? 'Previu could not load this page' : `${label} could not be displayed`}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 360, lineHeight: 1.6 }}>
          {isRoot
            ? 'Reloading usually clears it. If it keeps happening, send us the detail below.'
            : 'Something went wrong rendering this section. The rest of the page is unaffected.'}
          {this.state.error && (
            <details style={{ marginTop: 10, textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--red)' }}>
                Error detail
              </summary>
              <pre style={{
                fontSize: 11, marginTop: 6, whiteSpace: 'pre-wrap',
                color: 'var(--text-muted)', fontFamily: 'var(--font-mono)',
              }}>
                {this.state.error.toString()}
                {'\n\n'}build {BUILD_SHA_SHORT} · {BUILD_TIME}
              </pre>
            </details>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button className="btn btn-primary" type="button" onClick={this.handleReload}>
            Reload page
          </button>
          <button className="btn btn-ghost" type="button" onClick={this.handleReset}>
            Try again
          </button>
        </div>
      </div>
    );

    // At the root there is no page around the panel — centre it so the result
    // reads as a handled error rather than a half-drawn screen.
    if (!isRoot) return panel;
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-canvas)', padding: '24px 16px',
      }}>
        {panel}
      </div>
    );
  }
}
